/**
 * DemoConversionPrompt — contextual popup shown after key demo moments.
 *
 * Listens for `demo-conversion-event` DOM events fired by feature pages
 * after high-value actions (AI note generation, billing dashboard view,
 * scheduling an appointment, exploring client portal).  Shows every
 * PROMPT_INTERVAL actions — not on every navigation.
 *
 * Two variants:
 *   • Pre-claim: "Name Your Clinic" (opens ClaimOrgDialog)
 *   • Post-claim: "Continue Customizing / Start Free Trial"
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box
} from '@mui/material';
import SparkleIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useIsMobile } from '../../../hooks/useMediaQuery';

/** Session counter key — persists across page navigations within a tab. */
const PROMPT_SESSION_KEY = 'demo_conversion_prompt_count';
const PROMPT_DISMISSED_KEY = 'demo_conversion_prompt_dismissed';

/** Show the prompt every N high-value actions. */
const PROMPT_INTERVAL = 3;

/**
 * Custom DOM event name.  Feature pages fire this after high-value actions:
 *   window.dispatchEvent(new Event('demo-conversion-event'));
 */
const CONVERSION_EVENT = 'demo-conversion-event';

interface DemoConversionPromptProps {
  /** Optional callback to open the ClaimOrgDialog from the parent */
  onOpenClaimDialog?: () => void;
}

function DemoConversionPrompt({ onOpenClaimDialog }: DemoConversionPromptProps) {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const hasClaimed = !!user && user.claimed_org === true;
  // Show for any user still setting up their practice.  Gate is setup
  // completion, NOT subscription status — users see this whether or not
  // they have subscribed, until all clinic setup steps are done.
  const showPrompt = !!user && !user.setup_complete;

  const handleConversionEvent = useCallback(() => {
    if (!showPrompt) return;

    // Check if user permanently dismissed prompts this session
    const dismissed = sessionStorage.getItem(PROMPT_DISMISSED_KEY);
    if (dismissed === 'true') return;

    // Increment action counter
    const count = parseInt(sessionStorage.getItem(PROMPT_SESSION_KEY) || '0', 10) + 1;
    sessionStorage.setItem(PROMPT_SESSION_KEY, String(count));

    // Show prompt every N actions
    if (count % PROMPT_INTERVAL === 0) {
      setOpen(true);
    }
  }, [showPrompt]);

  useEffect(() => {
    window.addEventListener(CONVERSION_EVENT, handleConversionEvent);
    return () => window.removeEventListener(CONVERSION_EVENT, handleConversionEvent);
  }, [handleConversionEvent]);

  if (!showPrompt) return null;

  const handleDismiss = () => {
    setOpen(false);
  };

  const handleDontShowAgain = () => {
    sessionStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
    setOpen(false);
  };

  const handleStartTrial = () => {
    setOpen(false);
    navigate('/name-my-clinic');
  };

  const handleNameClinic = () => {
    setOpen(false);
    if (onOpenClaimDialog) {
      onOpenClaimDialog();
    }
  };

  const handleContinueCustomizing = () => {
    setOpen(false);
    navigate('/admin/setup-wizard');
  };

  // --- Variant: user HAS claimed an org, encourage customizing or trial ---
  if (hasClaimed) {
    return (
      <Dialog
        open={open}
        onClose={handleDismiss}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { ...(!isMobile && { borderRadius: 2 }) } }}
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
          <SparkleIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Your practice is taking shape!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Nice progress! Continue customizing your practice in the setup wizard, or
            start your <strong>14-day free trial</strong> to unlock everything.
          </Typography>
          <Box
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 1,
              p: 1.5,
              mt: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>What you get with a free trial:</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ m: 0, pl: 2 }}>
              <li>Invite your staff and send real email invitations</li>
              <li>Import client data securely with a BAA</li>
              <li>Enable payments and start billing</li>
              <li>14 days free — no charge until you decide</li>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleDontShowAgain} size="small" color="inherit">
            Don&apos;t show again
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleContinueCustomizing} size="small">
              Continue Customizing
            </Button>
            <Button onClick={handleStartTrial} variant="contained" size="small">
              Start Free Trial
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    );
  }

  // --- Variant: user has NOT claimed an org yet — encourage naming clinic ---
  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { ...(!isMobile && { borderRadius: 2 }) } }}
     fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
        <SparkleIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Like what you see?
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 1 }}>
          You&apos;re exploring the demo — great! Ready to make it yours?
          Name your clinic and we&apos;ll start setting up{' '}
          <strong>your own practice</strong> — you can even auto-fill from your website.
        </Typography>
        <Box
          sx={{
            bgcolor: 'grey.50',
            borderRadius: 1,
            p: 1.5,
            mt: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Start customizing now:</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" component="ul" sx={{ m: 0, pl: 2 }}>
            <li>Auto-fill staff, specialties &amp; appointment types from your website</li>
            <li>Set up your practice identity, locations &amp; branding</li>
            <li>Keep exploring the demo while you customize</li>
            <li>No commitment — upgrade to a free trial when you&apos;re ready</li>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button onClick={handleDontShowAgain} size="small" color="inherit">
          Don&apos;t show again
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleDismiss} size="small">
            Keep Exploring
          </Button>
          {onOpenClaimDialog ? (
            <Button onClick={handleNameClinic} variant="contained" size="small">
              Name Your Clinic
            </Button>
          ) : (
            <Button onClick={handleStartTrial} variant="contained" size="small">
              Start Free Trial
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default DemoConversionPrompt;
